import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NativeCheckbox } from '@/components/ui/checkbox';
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { resolveDealOwnerScope } from './dealOwnerScope.js';
import { DEFAULT_FILTERS } from './useDeals.js';
import { computePeriodDates } from '../lib/period.js';
import { Calendar as CalendarIcon, ChevronDown, Check, Search, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

const PERIOD_TYPES = ['year', 'month', 'quarter', 'custom'];
const PERIOD_TYPE_LABELS = { year: 'Year', month: 'Month', quarter: 'Quarter', custom: 'Custom' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function sameUserId(a, b) {
    if (a == null || b == null || a === '' || b === '') return false;
    return String(a) === String(b);
}

function countActiveFilters(f) {
    if (!f) return 0;
    let count = 0;
    if (f.scope === 'my') count++;
    if (f.ownerIds && f.ownerIds.length > 0) count++;
    if (f.accountIds && f.accountIds.length > 0) count++;
    const dim = f.dimension || 'created';
    const isDefaultTime =
        dim === 'created' &&
        f.createdPeriodType === 'year' &&
        (f.createdPeriodOffset === 0 || !f.createdPeriodOffset) &&
        !f.createdStart &&
        !f.createdEnd;
    if (!isDefaultTime) count++;
    return count;
}

function getTimeSummary(filters) {
    const dim = filters.dimension || 'created';
    const dimName = dim === 'revenue' ? 'Revenue Recognition' : 'Deals Created';
    const type = filters[`${dim}PeriodType`] || 'year';
    const offset = filters[`${dim}PeriodOffset`] || 0;

    let title = '';
    let subtitle = '';

    if (type === 'custom') {
        const start = filters[`${dim}Start`];
        const end = filters[`${dim}End`];
        if (start || end) {
            title = 'Custom Date Range';
            subtitle = `${dimName}: ${start || '…'} – ${end || '…'}`;
        } else {
            title = 'All Time / Custom Range';
            subtitle = `${dimName} (Custom)`;
        }
    } else {
        const dates = computePeriodDates(dim, type, offset);
        const year = dates.start?.slice(0, 4);
        const offsetText = offset === -1 ? 'Previous' : offset === 0 ? 'This' : 'Next';

        if (type === 'year') {
            title = `${offsetText} Year (${year})`;
            subtitle = `${dimName} · ${year}`;
        } else if (type === 'month') {
            const m = parseInt(dates.start?.slice(5, 7), 10) - 1;
            title = `${offsetText} Month (${MONTHS[m]} ${year})`;
            subtitle = `${dimName} · ${MONTHS[m]} ${year}`;
        } else if (type === 'quarter') {
            const sm = parseInt(dates.start?.slice(5, 7), 10) - 1;
            const qNum = Math.floor(sm / 3) + 1;
            title = `${offsetText} Quarter (Q${qNum} ${year})`;
            subtitle = `${dimName} · Q${qNum} ${year}`;
        }
    }
    return { title, subtitle };
}

export default function FilterPanel({
    filters,
    setFilter,
    users,
    clients,
    open = false,
    collapsed,
    onOpenChange,
    onScopeResolved,
}) {
    // Support both open prop and legacy collapsed prop
    const isOpen = open !== undefined ? open : !collapsed;

    const [beyondOwn, setBeyondOwn] = useState(true);
    const [scopedOwners, setScopedOwners] = useState(users || []);
    const [scopedOwnerIds, setScopedOwnerIds] = useState([]);
    const [draftFilters, setDraftFilters] = useState(() => ({ ...filters }));
    const [timeExpanded, setTimeExpanded] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [ownerSearch, setOwnerSearch] = useState('');
    const [accountSearch, setAccountSearch] = useState('');
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.innerWidth < 768
    );

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const handler = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // Sync draft when opened
    useEffect(() => {
        if (isOpen) {
            setDraftFilters({ ...filters });
            setOpenDropdown(null);
            setOwnerSearch('');
            setAccountSearch('');
        }
    }, [isOpen, filters]);

    useEffect(() => {
        let cancelled = false;
        resolveDealOwnerScope(users).then((scope) => {
            if (cancelled) return;
            setBeyondOwn(scope.beyondOwn);
            setScopedOwners(scope.scopedOwners);
            setScopedOwnerIds(scope.scopedOwnerIds);
            onScopeResolved?.(scope);
            if (!scope.beyondOwn) {
                setDraftFilters((prev) => ({ ...prev, scope: 'all', ownerIds: [] }));
            }
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users]);

    const dim = draftFilters.dimension || 'created';
    const ownerOptions = scopedOwners;

    const accountOptions = useMemo(() => {
        const selectedOwners = draftFilters.ownerIds || [];
        let ownerIds = selectedOwners;
        if (!ownerIds.length) {
            ownerIds = scopedOwnerIds;
        }
        if (ownerIds.length) {
            return (clients || []).filter(
                (c) => c.ownerUserId && ownerIds.some((id) => sameUserId(id, c.ownerUserId))
            );
        }
        return clients || [];
    }, [clients, draftFilters.ownerIds, scopedOwnerIds]);

    const filteredOwnerOptions = useMemo(() => {
        if (!ownerSearch.trim()) return ownerOptions;
        const q = ownerSearch.toLowerCase();
        return ownerOptions.filter((u) => {
            const name = (u.name || u.fullName || u.email || '').toLowerCase();
            return name.includes(q);
        });
    }, [ownerOptions, ownerSearch]);

    const filteredAccountOptions = useMemo(() => {
        if (!accountSearch.trim()) return accountOptions;
        const q = accountSearch.toLowerCase();
        return accountOptions.filter((c) => {
            const name = (c.name || c.client_name || '').toLowerCase();
            return name.includes(q);
        });
    }, [accountOptions, accountSearch]);

    const isDraftOwnerSelected = (id) =>
        !draftFilters.ownerIds?.length ||
        draftFilters.ownerIds.some((oid) => sameUserId(oid, id));

    const isDraftAccountSelected = (id) =>
        !draftFilters.accountIds?.length ||
        draftFilters.accountIds.some((aid) => sameUserId(aid, id));

    const draftOwnerCount = draftFilters.ownerIds?.length || 0;
    const ownerTotal = ownerOptions.length || 1;
    const ownerLabel =
        draftOwnerCount === 0 || draftOwnerCount >= ownerTotal
            ? 'All Sales Reps'
            : draftOwnerCount === 1
            ? ownerOptions.find((u) => sameUserId(u.id, draftFilters.ownerIds[0]))?.name ||
              '1 Sales Rep'
            : `${draftOwnerCount} selected`;

    const draftAccountCount = draftFilters.accountIds?.length || 0;
    const accountTotal = accountOptions.length || 1;
    const accountLabel =
        draftAccountCount === 0 || draftAccountCount >= accountTotal
            ? 'All Accounts'
            : draftAccountCount === 1
            ? accountOptions.find((c) => sameUserId(c.id, draftFilters.accountIds[0]))?.name ||
              accountOptions.find((c) => sameUserId(c.id, draftFilters.accountIds[0]))
                  ?.client_name ||
              '1 Account'
            : `${draftAccountCount} selected`;

    const toggleDraftOwner = (id) => {
        const current = draftFilters.ownerIds || [];
        let next;
        if (current.length === 0) {
            next = ownerOptions.map((o) => o.id).filter((oid) => !sameUserId(oid, id));
        } else if (current.some((oid) => sameUserId(oid, id))) {
            next = current.filter((oid) => !sameUserId(oid, id));
        } else {
            next = [...current, id];
        }
        if (next.length === ownerOptions.length) next = [];
        setDraftFilters((prev) => ({ ...prev, ownerIds: next, accountIds: [] }));
    };

    const toggleDraftAccount = (id) => {
        const current = draftFilters.accountIds || [];
        let next;
        if (current.length === 0) {
            next = accountOptions.map((o) => o.id).filter((aid) => !sameUserId(aid, id));
        } else if (current.some((aid) => sameUserId(aid, id))) {
            next = current.filter((aid) => !sameUserId(aid, id));
        } else {
            next = [...current, id];
        }
        if (next.length === accountOptions.length) next = [];
        setDraftFilters((prev) => ({ ...prev, accountIds: next }));
    };

    const handleApply = useCallback(() => {
        setFilter(draftFilters);
        onOpenChange?.(false);
    }, [draftFilters, setFilter, onOpenChange]);

    const handleClearAll = useCallback(() => {
        setDraftFilters({ ...DEFAULT_FILTERS });
    }, []);

    const draftActiveCount = useMemo(() => countActiveFilters(draftFilters), [draftFilters]);
    const { title: timeSummaryTitle, subtitle: timeSummarySubtitle } = useMemo(
        () => getTimeSummary(draftFilters),
        [draftFilters]
    );

    return (
        <Sheet open={isOpen} onOpenChange={onOpenChange}>
            <SheetContent
                side={isMobile ? 'bottom' : 'left'}
                className={cn(
                    'p-0 flex flex-col bg-background focus:outline-none z-[600]',
                    isMobile
                        ? 'h-[85vh] max-h-[88vh] rounded-t-2xl border-t shadow-2xl'
                        : 'w-full sm:max-w-md h-full border-r shadow-xl'
                )}
            >
                {isMobile && (
                    <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full mx-auto mt-2.5 mb-1 shrink-0" />
                )}

                <SheetHeader className="px-5 py-4 border-b text-left shrink-0">
                    <div className="flex items-center justify-between pr-6">
                        <div>
                            <SheetTitle className="text-base font-bold text-foreground">
                                Filter Deals
                            </SheetTitle>
                            <SheetDescription className="text-xs text-muted-foreground mt-0.5">
                                Refine deals by scope, time period, sales reps, and accounts.
                            </SheetDescription>
                        </div>
                        {draftActiveCount > 0 && (
                            <Badge variant="secondary" className="text-xs font-semibold px-2 py-0.5">
                                {draftActiveCount} active
                            </Badge>
                        )}
                    </div>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" id="filterDrawerBody">
                    {/* Deal Scope */}
                    {beyondOwn && (
                        <div className="space-y-1.5" id="filterBlockDealScope">
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                Deal Scope
                            </label>
                            <div className="grid grid-cols-2 p-1 bg-muted rounded-lg gap-1">
                                <button
                                    type="button"
                                    className={cn(
                                        'py-1.5 text-xs font-medium rounded-md transition-all',
                                        draftFilters.scope !== 'my'
                                            ? 'bg-background text-foreground shadow-sm font-semibold'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                    data-scope="all"
                                    onClick={() => setDraftFilters((d) => ({ ...d, scope: 'all' }))}
                                >
                                    All Deals
                                </button>
                                <button
                                    type="button"
                                    className={cn(
                                        'py-1.5 text-xs font-medium rounded-md transition-all',
                                        draftFilters.scope === 'my'
                                            ? 'bg-background text-foreground shadow-sm font-semibold'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                    data-scope="my"
                                    onClick={() => setDraftFilters((d) => ({ ...d, scope: 'my' }))}
                                >
                                    My Deals
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Time Dimension (Single Collapsed Control with Expandable Preset Settings) */}
                    <div className="space-y-1.5" id="filterBlockTimeDimension">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Time Dimension
                        </label>
                        <div className="border border-input rounded-lg overflow-hidden bg-card/60 transition-colors">
                            <button
                                type="button"
                                className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/40 transition-colors"
                                onClick={() => setTimeExpanded((prev) => !prev)}
                                aria-expanded={timeExpanded}
                                id="timeDimensionTrigger"
                            >
                                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                    <div className="size-7 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                        <CalendarIcon className="size-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-xs font-semibold text-foreground truncate">
                                            {timeSummaryTitle}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground truncate">
                                            {timeSummarySubtitle}
                                        </div>
                                    </div>
                                </div>
                                <ChevronDown
                                    className={cn(
                                        'size-4 text-muted-foreground transition-transform duration-200 shrink-0',
                                        timeExpanded && 'rotate-180'
                                    )}
                                />
                            </button>

                            {timeExpanded && (
                                <div className="p-3 border-t bg-muted/20 space-y-3.5">
                                    {/* Dimension Switch */}
                                    <div className="space-y-1.5">
                                        <span className="text-[11px] font-medium text-muted-foreground">
                                            Basis
                                        </span>
                                        <div className="grid grid-cols-2 p-0.5 bg-muted rounded-md gap-1">
                                            <button
                                                type="button"
                                                className={cn(
                                                    'py-1.5 text-xs rounded transition-all',
                                                    dim === 'created'
                                                        ? 'bg-background font-semibold text-foreground shadow-sm'
                                                        : 'text-muted-foreground hover:text-foreground'
                                                )}
                                                onClick={() =>
                                                    setDraftFilters((d) => ({ ...d, dimension: 'created' }))
                                                }
                                            >
                                                Deals Created
                                            </button>
                                            <button
                                                type="button"
                                                className={cn(
                                                    'py-1.5 text-xs rounded transition-all',
                                                    dim === 'revenue'
                                                        ? 'bg-background font-semibold text-foreground shadow-sm'
                                                        : 'text-muted-foreground hover:text-foreground'
                                                )}
                                                onClick={() =>
                                                    setDraftFilters((d) => ({ ...d, dimension: 'revenue' }))
                                                }
                                            >
                                                Revenue Recognition
                                            </button>
                                        </div>
                                    </div>

                                    {/* Period Granularity */}
                                    <div className="space-y-1.5">
                                        <span className="text-[11px] font-medium text-muted-foreground">
                                            Granularity
                                        </span>
                                        <div className="grid grid-cols-4 p-0.5 bg-muted rounded-md gap-1">
                                            {PERIOD_TYPES.map((t) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    className={cn(
                                                        'py-1 text-xs rounded transition-all',
                                                        draftFilters[`${dim}PeriodType`] === t
                                                            ? 'bg-background font-semibold text-foreground shadow-sm'
                                                            : 'text-muted-foreground hover:text-foreground'
                                                    )}
                                                    onClick={() => {
                                                        setDraftFilters((d) => ({
                                                            ...d,
                                                            [`${dim}PeriodType`]: t,
                                                            ...(t === 'custom'
                                                                ? {}
                                                                : {
                                                                      [`${dim}Start`]: '',
                                                                      [`${dim}End`]: '',
                                                                  }),
                                                        }));
                                                    }}
                                                >
                                                    {PERIOD_TYPE_LABELS[t]}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Offset Navigation (When not custom) */}
                                    {draftFilters[`${dim}PeriodType`] !== 'custom' && (
                                        <div className="space-y-1.5">
                                            <span className="text-[11px] font-medium text-muted-foreground">
                                                Range
                                            </span>
                                            <div className="grid grid-cols-3 p-0.5 bg-muted rounded-md gap-1">
                                                {[-1, 0, 1].map((off) => (
                                                    <button
                                                        key={off}
                                                        type="button"
                                                        className={cn(
                                                            'py-1 text-xs rounded transition-all',
                                                            draftFilters[`${dim}PeriodOffset`] === off
                                                                ? 'bg-background font-semibold text-foreground shadow-sm'
                                                                : 'text-muted-foreground hover:text-foreground'
                                                        )}
                                                        onClick={() =>
                                                            setDraftFilters((d) => ({
                                                                ...d,
                                                                [`${dim}PeriodOffset`]: off,
                                                            }))
                                                        }
                                                    >
                                                        {off === -1
                                                            ? '← Previous'
                                                            : off === 0
                                                            ? 'This'
                                                            : 'Next →'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Custom Date Inputs */}
                                    {draftFilters[`${dim}PeriodType`] === 'custom' && (
                                        <div className="space-y-2 pt-1">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div className="space-y-1">
                                                    <span className="text-[11px] font-medium text-muted-foreground">
                                                        From
                                                    </span>
                                                    <Input unstyled type="date" className="form-input" aria-label={`${dim} from`} value={draftFilters[`${dim}Start`] || ''} onChange={(e) => setDraftFilters((d) => ({ ...d, [`${dim}Start`]: e.target.value }))} />
                                                </div>
                                                <div className="space-y-1">
                                                    <span className="text-[11px] font-medium text-muted-foreground">
                                                        To
                                                    </span>
                                                    <Input unstyled type="date" className="form-input" aria-label={`${dim} to`} value={draftFilters[`${dim}End`] || ''} onChange={(e) => setDraftFilters((d) => ({ ...d, [`${dim}End`]: e.target.value }))} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Filter By Section */}
                    <div className="space-y-3.5 pt-1">
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Filter By
                        </div>

                        {/* Sales Rep Dropdown */}
                        {beyondOwn && (
                            <div className="space-y-1.5" id="filterBlockSalesRep">
                                <label className="text-xs font-medium text-foreground">
                                    Sales Rep
                                </label>
                                <Popover
                                    open={openDropdown === 'owner'}
                                    onOpenChange={(pOpen) => setOpenDropdown(pOpen ? 'owner' : null)}
                                >
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full justify-between h-9 px-3 text-xs font-normal border-input"
                                            id="ownerSelectTrigger"
                                        >
                                            <span className="truncate">{ownerLabel}</span>
                                            <ChevronDown className="size-4 opacity-50 shrink-0 ml-2" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-[var(--radix-popover-trigger-width)] p-2 z-[700]"
                                        align="start"
                                    >
                                        <div className="relative mb-2 flex items-center">
                                            <Search className="absolute left-2.5 size-3.5 text-muted-foreground pointer-events-none" />
                                            <Input
                                                type="search"
                                                placeholder="Search reps..."
                                                value={ownerSearch}
                                                onChange={(e) => setOwnerSearch(e.target.value)}
                                                className="h-8 !pl-9 text-xs"
                                            />
                                        </div>
                                        <div className="space-y-1 max-h-56 overflow-y-auto overscroll-contain touch-pan-y pr-1 scrollbar-thin" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }} onWheel={(e) => e.stopPropagation()}>
                                            <label className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted cursor-pointer min-h-[40px] text-xs font-medium">
                                                <NativeCheckbox
                                                    checked={draftOwnerCount === 0}
                                                    onChange={() =>
                                                        setDraftFilters((d) => ({
                                                            ...d,
                                                            ownerIds: [],
                                                            accountIds: [],
                                                        }))
                                                    }
                                                />
                                                <span>All Sales Reps</span>
                                            </label>
                                            <div className="h-px bg-border my-1" />
                                            {filteredOwnerOptions.length === 0 ? (
                                                <div className="py-2 px-2 text-xs text-muted-foreground text-center">
                                                    No reps found
                                                </div>
                                            ) : (
                                                filteredOwnerOptions.map((u) => (
                                                    <label
                                                        key={u.id}
                                                        className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted cursor-pointer min-h-[40px] text-xs"
                                                    >
                                                        <NativeCheckbox
                                                            checked={isDraftOwnerSelected(u.id)}
                                                            onChange={() => toggleDraftOwner(u.id)}
                                                        />
                                                        <span className="truncate">
                                                            {u.name || u.fullName || u.email || u.id}
                                                        </span>
                                                    </label>
                                                ))
                                            )}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )}

                        {/* Account Dropdown */}
                        <div className="space-y-1.5" id="filterBlockAccount">
                            <label className="text-xs font-medium text-foreground">
                                Account
                            </label>
                            <Popover
                                open={openDropdown === 'account'}
                                onOpenChange={(pOpen) => setOpenDropdown(pOpen ? 'account' : null)}
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full justify-between h-9 px-3 text-xs font-normal border-input"
                                        id="accountSelectTrigger"
                                    >
                                        <span className="truncate">{accountLabel}</span>
                                        <ChevronDown className="size-4 opacity-50 shrink-0 ml-2" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[var(--radix-popover-trigger-width)] p-2 z-[700]"
                                    align="start"
                                >
                                    <div className="relative mb-2 flex items-center">
                                        <Search className="absolute left-2.5 size-3.5 text-muted-foreground pointer-events-none" />
                                        <Input
                                            type="search"
                                            placeholder="Search accounts..."
                                            value={accountSearch}
                                            onChange={(e) => setAccountSearch(e.target.value)}
                                            className="h-8 !pl-9 text-xs"
                                        />
                                    </div>
                                    <div className="space-y-1 max-h-56 overflow-y-auto overscroll-contain touch-pan-y pr-1 scrollbar-thin" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }} onWheel={(e) => e.stopPropagation()}>
                                        <label className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted cursor-pointer min-h-[40px] text-xs font-medium">
                                            <NativeCheckbox
                                                checked={draftAccountCount === 0}
                                                onChange={() =>
                                                    setDraftFilters((d) => ({
                                                        ...d,
                                                        accountIds: [],
                                                    }))
                                                }
                                            />
                                            <span>All Accounts</span>
                                        </label>
                                        <div className="h-px bg-border my-1" />
                                        {filteredAccountOptions.length === 0 ? (
                                            <div className="py-2 px-2 text-xs text-muted-foreground text-center">
                                                No accounts found
                                            </div>
                                        ) : (
                                            filteredAccountOptions.map((c) => (
                                                <label
                                                    key={c.id}
                                                    className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted cursor-pointer min-h-[40px] text-xs"
                                                >
                                                    <NativeCheckbox
                                                        checked={isDraftAccountSelected(c.id)}
                                                        onChange={() => toggleDraftAccount(c.id)}
                                                    />
                                                    <span className="truncate">
                                                        {c.name || c.client_name}
                                                    </span>
                                                </label>
                                            ))
                                        )}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>
                </div>

                {/* Sticky Footer */}
                <SheetFooter className="p-4 border-t bg-background/95 backdrop-blur shrink-0 flex flex-row items-center justify-between gap-3 sticky bottom-0 z-10">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs text-muted-foreground hover:text-foreground h-9 px-3"
                        onClick={handleClearAll}
                        id="filterDrawerClearAll"
                    >
                        <RotateCcw className="size-3.5 mr-1.5 opacity-70" />
                        Clear all
                    </Button>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs h-9 px-3"
                            onClick={() => onOpenChange?.(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="default"
                            size="sm"
                            className="text-xs font-semibold h-9 px-5"
                            onClick={handleApply}
                            id="filterDrawerApply"
                        >
                            Apply
                        </Button>
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
